/**
 * Media URL Resolution
 *
 * Transforms various media pointer formats into API-resolvable URLs.
 *
 * Supported formats:
 *   file-service://file-{ID}      — old-style OpenAI (uploads, DALL-E)
 *   sediment://file_{hash}         — new-style OpenAI (user-{userID}/ folder)
 *   file-{ID}                      — bare attachment ID (from metadata.attachments)
 *   file_{hash}                    — bare sediment hash
 *
 * @module @humanizer/studio/utils/media-url
 */

/**
 * Normalize any media reference to a canonical pointer URL.
 * Bare IDs get wrapped in the appropriate protocol.
 */
function normalizeToPointer(raw: string): string {
  if (raw.startsWith('file-service://') || raw.startsWith('sediment://')) {
    return raw;
  }
  // file_{hex-hash} → sediment://
  if (raw.startsWith('file_')) {
    return `sediment://${raw}`;
  }
  // file-{ID} → file-service://
  if (raw.startsWith('file-')) {
    return `file-service://${raw}`;
  }
  // Anything else — wrap as file-service://file-{raw}
  return `file-service://file-${raw}`;
}

/**
 * Check if running in Electron (evaluated dynamically to handle preload timing)
 */
function getIsElectron(): boolean {
  return typeof window !== 'undefined' && !!window.isElectron;
}

/**
 * Extract the media pointer from a serve URL (either web or Electron).
 * Returns the decoded pointer string, or null if not a recognized serve URL.
 *
 * Recognized formats:
 *   Web:      /api/media/serve-by-pointer?pointer=<encoded>
 *   Electron: humanizer-media://serve?p=<encoded>
 */
export function extractPointerFromSrc(src: string): string | null {
  // Web: /api/media/serve-by-pointer?pointer=...
  const webMatch = src.match(/[?&]pointer=([^&]+)/);
  if (webMatch) return decodeURIComponent(webMatch[1]);

  // Electron: humanizer-media://serve?p=...
  // Note: Chromium normalizes the URL by adding a trailing slash after the hostname
  // (serve → serve/) so we match with an optional slash: serve/?p=
  const electronMatch = src.match(/humanizer-media:\/\/serve\/?\?p=([^&]+)/);
  if (electronMatch) return decodeURIComponent(electronMatch[1]);

  return null;
}

/**
 * Resolve a media URL from various pointer formats to an API-servable URL.
 * In Electron mode, uses the humanizer-media:// custom protocol.
 * In web mode, uses the /api/media/ endpoints.
 */
export function resolveMediaUrl(url: string | undefined): string {
  if (!url) return '';

  // Check Electron status dynamically (preload may not have run at module load time)
  const isElectron = getIsElectron();

  // Known protocol pointers and bare file refs
  if (
    url.startsWith('file-service://') ||
    url.startsWith('sediment://') ||
    url.startsWith('file-') ||
    url.startsWith('file_')
  ) {
    const pointer = normalizeToPointer(url);

    // In Electron, use custom protocol for local media
    // Use path-based URL (humanizer-media://local/...) to preserve case sensitivity
    // (hostnames get lowercased by Chromium, breaking case-sensitive file IDs)
    if (isElectron) {
      return `humanizer-media://serve?p=${encodeURIComponent(pointer)}`;
    }

    // In web, use API endpoint
    return `/api/media/serve-by-pointer?pointer=${encodeURIComponent(pointer)}`;
  }

  // Already absolute HTTP(S) URL — passthrough
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Already an API path
  if (url.startsWith('/api/')) {
    // In Electron without cloud, these won't work - return placeholder
    if (isElectron) {
      // Extract the media ID from the URL if possible
      const match = url.match(/\/api\/media\/serve\/([^?]+)/);
      if (match) {
        return `humanizer-media://serve?p=${encodeURIComponent(match[1])}`;
      }
    }
    return url;
  }

  // Data URIs — passthrough
  if (url.startsWith('data:')) {
    return url;
  }

  // humanizer-media protocol — passthrough
  if (url.startsWith('humanizer-media://')) {
    return url;
  }

  // Relative path
  if (isElectron) {
    // In Electron, try to resolve as local media
    return `humanizer-media://serve?p=${encodeURIComponent(url)}`;
  }

  // In web, resolve via API
  return `/api/media/by-path?path=${encodeURIComponent(url)}`;
}

/**
 * Inject media references into text as markdown image syntax.
 * Strips [Object Object] artifacts and appends image markdown for each media ref.
 *
 * Idempotent: skips refs whose pointer already appears in the text
 * (as markdown image, raw pointer, or asset_pointer dict).
 */
export function injectMediaRefs(
  text: string,
  mediaRefs?: Array<{ id: string; type: string }>
): string {
  // Strip [Object Object] / [object Object] artifacts
  let cleaned = text.replace(/\[(?:O|o)bject(?:,?\s*(?:O|o)bject)*\]/g, '').trim();

  if (!mediaRefs?.length) return cleaned;

  // Separate by media type — exclude audio
  const imageRefs = mediaRefs.filter(r => r.type !== 'audio' && r.type !== 'video');
  const videoRefs = mediaRefs.filter(r => r.type === 'video');

  if (imageRefs.length === 0 && videoRefs.length === 0) return cleaned;

  // Only append refs whose pointer is NOT already present in the text
  const newImageRefs = imageRefs.filter(ref => {
    const pointer = normalizeToPointer(ref.id);
    const resolved = resolveMediaUrl(ref.id);
    return !cleaned.includes(ref.id) && !cleaned.includes(pointer) && !cleaned.includes(resolved);
  });

  const newVideoRefs = videoRefs.filter(ref => {
    const pointer = normalizeToPointer(ref.id);
    const resolved = resolveMediaUrl(ref.id);
    return !cleaned.includes(ref.id) && !cleaned.includes(pointer) && !cleaned.includes(resolved);
  });

  if (newImageRefs.length === 0 && newVideoRefs.length === 0) return cleaned;

  const parts: string[] = [];

  if (newImageRefs.length > 0) {
    parts.push(newImageRefs.map(ref => `![image](${resolveMediaUrl(ref.id)})`).join('\n'));
  }

  if (newVideoRefs.length > 0) {
    parts.push(newVideoRefs.map(ref =>
      `<video controls preload="metadata" style="max-width:100%;border-radius:8px"><source src="${resolveMediaUrl(ref.id)}"></video>`
    ).join('\n'));
  }

  return cleaned + '\n\n' + parts.join('\n\n');
}

/**
 * Convert raw structured content dicts (from ChatGPT export) into renderable content.
 * These appear as Python-style dict strings in chunk content:
 *   - image_asset_pointer → markdown image
 *
 * Handles nested dicts (metadata with sub-objects) by matching the
 * asset_pointer value directly rather than trying to parse the full dict.
 */
function convertStructuredContent(text: string): string {
  // If the text looks like an image_asset_pointer dict, extract the pointer
  // and replace the entire text (these are typically standalone chunk content)
  if (/['"]content_type['"]:\s*['"]image_asset_pointer['"]/.test(text)) {
    const pointerMatch = text.match(/['"]asset_pointer['"]:\s*['"]([^'"]+)['"]/);
    if (pointerMatch) {
      return `![image](${pointerMatch[1]})`;
    }
  }
  return text;
}

/**
 * Pre-process markdown to resolve media URLs in image syntax.
 * Catches all known pointer formats in ![alt](pointer) patterns,
 * and converts raw image_asset_pointer dicts to markdown images.
 */
export function resolveMediaInMarkdown(text: string): string {
  // First: convert raw asset_pointer dicts to markdown image syntax
  let result = convertStructuredContent(text);

  // Then: resolve all pointer-based image URLs to API serve endpoints,
  // deduplicating so each pointer only produces one image tag.
  const seenPointers = new Set<string>();
  result = result.replace(
    /!\[([^\]]*)\]\((file-service:\/\/[^)]+|sediment:\/\/[^)]+|file[-_][^)]+)\)/g,
    (match, alt, url) => {
      const normalized = normalizeToPointer(url);
      if (seenPointers.has(normalized)) {
        return ''; // Remove duplicate image
      }
      seenPointers.add(normalized);
      return `![${alt}](${resolveMediaUrl(url)})`;
    }
  );

  // Clean up blank lines left by removed duplicates
  result = result.replace(/\n{3,}/g, '\n\n');

  return result;
}
